import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

const links = [
  { href: "#rooms", label: "Rooms" },
  { href: "#amenities", label: "Amenities" },
  { href: "#promotions", label: "Offers" },
  { href: "#things-to-do", label: "Things to Do" },
  { href: "#dining", label: "Dining" },
  { href: "#visitor-guide", label: "Visitor Guide" },
  { href: "#gallery", label: "Gallery" },
  { href: "#location", label: "Find Us" },
];

export const Navbar = ({ hotel, onEnquire }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0A192F]/90 backdrop-blur-xl border-b border-[#C5A059]/20 py-3" : "bg-transparent py-5"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a href="#top" className="flex flex-col leading-none" data-testid="nav-brand-link">
          <span className="font-serif-display text-xl sm:text-2xl font-bold text-[#F8F6F0] tracking-tight">{hotel?.name || "The Grand Eufaula Haven"}</span>
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059] mt-1">Eufaula · Alabama</span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="gold-underline text-sm font-medium text-[#F8F6F0]/85 hover:text-[#F8F6F0] transition-colors" data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={onEnquire}
            className="text-sm font-medium text-[#F8F6F0]/85 hover:text-[#C5A059] transition-colors px-3 py-2"
            data-testid="nav-enquire-button"
          >
            Enquire
          </button>
          <a
            href={hotel?.phone_href || "tel:+13346870000"}
            className="bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] px-5 py-2.5 rounded-full text-sm font-semibold shadow-md transition-all flex items-center gap-2"
            data-testid="nav-call-to-book-button"
          >
            <Phone size={15} /> Call to Book
          </a>
        </div>

        <button className="lg:hidden text-[#F8F6F0]" onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-button" aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A192F]/97 backdrop-blur-xl border-t border-[#C5A059]/20 mt-3 px-6 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-[#F8F6F0]/90 text-base font-medium" data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </a>
          ))}
          <div className="flex gap-3 pt-3 border-t border-[#F8F6F0]/10">
            <button onClick={() => { setOpen(false); onEnquire(); }} className="flex-1 text-center text-sm font-medium text-[#F8F6F0] border border-[#C5A059]/50 rounded-full py-3" data-testid="nav-mobile-enquire-button">
              Enquire Online
            </button>
            <a href={hotel?.phone_href || "tel:+13346870000"} className="flex-1 text-center bg-[#C5A059] text-[#0A192F] rounded-full py-3 text-sm font-semibold" data-testid="nav-mobile-call-button">
              Call to Book
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
};
